import logibudEmblem from "../assets/integrations/logibud-emblem.webp";
import logibudLogo from "../assets/logo/logibud_primary_darkblue.svg";
import tripletexEmblem from "../assets/integrations/tripletex-emblem.svg";
import tripletexLogo from "../assets/integrations/tripletex-logo.svg";
import shopifyLogo from "../assets/integrations/shopify-logo.svg";

export type IntegrationKey = "tripletex" | "shopify" | "logibud-mcp";
export type IntegrationCategory = "accounting" | "commerce" | "automation";
export type IntegrationEnvironment = "test" | "production";

export interface ExternalOrderLinkParams {
  environment: IntegrationEnvironment;
  externalCompanyId?: string | null;
  externalOrderId?: string | null;
}

export interface IntegrationDefinition {
  key: IntegrationKey;
  providerKey?: string;
  name: string;
  category: IntegrationCategory;
  logoSrc: string;
  emblemSrc?: string;
  cardAsset: "logo" | "emblem";
  buildExternalOrderUrl?: (
    params: ExternalOrderLinkParams,
  ) => string | undefined;
}

const buildTripletexOrderUrl = ({
  environment,
  externalCompanyId,
  externalOrderId,
}: ExternalOrderLinkParams): string | undefined => {
  if (!externalCompanyId || !externalOrderId) return undefined;

  const baseUrl =
    environment === "test"
      ? "https://api-test.tripletex.tech"
      : "https://tripletex.no";
  const url = new URL("/page/order/details", baseUrl);
  url.searchParams.set("id", externalOrderId);
  url.searchParams.set("contextId", externalCompanyId);
  return url.toString();
};

export const integrationCatalog: Record<IntegrationKey, IntegrationDefinition> =
  {
    tripletex: {
      key: "tripletex",
      providerKey: "tripletex",
      name: "Tripletex",
      category: "accounting",
      logoSrc: tripletexLogo,
      emblemSrc: tripletexEmblem,
      cardAsset: "emblem",
      buildExternalOrderUrl: buildTripletexOrderUrl,
    },
    shopify: {
      key: "shopify",
      providerKey: "shopify",
      name: "Shopify",
      category: "commerce",
      logoSrc: shopifyLogo,
      cardAsset: "logo",
    },
    "logibud-mcp": {
      key: "logibud-mcp",
      name: "Logibud MCP",
      category: "automation",
      logoSrc: logibudLogo,
      emblemSrc: logibudEmblem,
      cardAsset: "emblem",
    },
  };

export const getIntegrationByProviderKey = (
  providerKey?: string | null,
): IntegrationDefinition | undefined =>
  Object.values(integrationCatalog).find(
    (integration) => integration.providerKey === providerKey,
  );

export const getExternalOrderUrl = (
  providerKey: string | null | undefined,
  params: ExternalOrderLinkParams,
): string | undefined =>
  getIntegrationByProviderKey(providerKey)?.buildExternalOrderUrl?.(params);
